/**
 * ═══════════════════════════════════════════════════════════════════════════
 * IMAGE STORAGE OPERATIONS (USER-FOLDER STRUCTURE)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Handles image storage with:
 * - Supabase Storage (binary files, not base64 in database)
 * - User-folder isolation (brand-assets/{userId}/file.png)
 * - Unique immutable filenames (historical invoices remain valid)
 * - Soft delete with 30-day recovery
 * - Multi-user isolation (RLS enforced via storage policies)
 * - Validation and security
 *
 * STORAGE STRUCTURE:
 * ```
 * brand-assets/
 *   {user_id_1}/
 *     logo_img_abc123.png
 *     logo_img_def456.png      (after logo update)
 *     signature_img_xyz789.png
 *     watermark_img_pqr012.png
 *   {user_id_2}/
 *     logo_img_aaa111.png
 *     signature_img_bbb222.png
 * ```
 *
 * ADMIN DELETION WORKFLOW:
 * 1. SQL Editor: SELECT permanently_delete_user('user-id-here');
 * 2. Storage Dashboard → brand-assets → Delete folder: {user_id}/
 * 3. Done ✅
 *
 * FILE NAMING:
 * - Format: {userId}/{type}_{imageId}.{ext}
 * - Example: a1b2-uuid/logo_img_1234_abc.png
 * - Never overwritten (each upload creates new file)
 * - Old URLs remain valid (historical invoices work forever)
 *
 * ATOMICITY:
 * - Upload to storage FIRST (fails fast if storage issue)
 * - Then metadata saved to database
 * - If metadata save fails: storage file orphaned (acceptable - cleaned during user deletion)
 * - If storage fails: error thrown immediately (no DB changes)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase, getCurrentUserId } from "../core/supabase.js";
import { generateId, getTimestamp } from "../core/utils.js";
import { requireFeature, requireLimit } from '../core/permissions.js';
import { FEATURES, LIMITS } from '../features/featureList.js';

// ══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ══════════════════════════════════════════════════════════════════════════════

const STORAGE_BUCKET = 'brand-assets';

const IMAGE_VALIDATION = {
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png'],
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5 MB
  MIN_FILE_SIZE: 50, // 50 bytes
};

const RECOVERY_PERIOD_DAYS = 30;

// ══════════════════════════════════════════════════════════════════════════════
// IMAGE UPLOAD (MAIN FUNCTION)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Upload image to Supabase Storage with user-folder structure
 *
 * Flow:
 * 1. Validate file
 * 2. Generate unique filename: {userId}/{type}_{imageId}.{ext}
 * 3. Upload to storage (RLS enforces user folder)
 * 4. Save metadata to database
 * 5. Return public URL
 *
 * ACID Considerations:
 * - Storage upload and DB insert are separate systems (not in single transaction)
 * - If storage succeeds but DB fails: orphaned file (acceptable - cleaned during user deletion)
 * - If DB succeeds but storage failed: already thrown error before DB insert
 * - This two-step process is inherent to storage architecture
 *
 * @param {File} file - Browser File object
 * @param {string} type - 'logo' | 'signature' | 'watermark'
 * @returns {Promise<string>} Public storage URL
 */
export async function uploadImage(file, type) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  if (!file) {
    throw new Error('File is required');
  }

  // ────────────────────────────────────────────────────────────────────────
  // STEP 1: VALIDATE FILE
  // ────────────────────────────────────────────────────────────────────────

  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  console.log(`✓ File validated: ${file.name} (${validation.sizeMB} MB)`);

  // ────────────────────────────────────────────────────────────────────────
  // STEP 2: GENERATE UNIQUE FILENAME WITH USER FOLDER
  // ────────────────────────────────────────────────────────────────────────

  const userId = await getCurrentUserId();
  const imageId = generateId("img");
  const fileExt = getFileExtension(file.name);
  const fileName = `${userId}/${type}_${imageId}.${fileExt}`;

  console.log(`✓ Upload path: ${fileName}`);

  // ────────────────────────────────────────────────────────────────────────
  // STEP 3: UPLOAD TO SUPABASE STORAGE (FAIL-FAST)
  // ────────────────────────────────────────────────────────────────────────
  // Storage policies enforce user can only upload to their own folder
  // If upload fails, error thrown immediately (no DB changes)
  // ────────────────────────────────────────────────────────────────────────

  const storageUrl = await uploadToStorage(file, fileName);

  console.log(`✓ Uploaded to storage: ${storageUrl}`);

  // ────────────────────────────────────────────────────────────────────────
  // STEP 4: SAVE METADATA TO DATABASE
  // ────────────────────────────────────────────────────────────────────────
  // Hash stored for reference only (not used in file path)
  // If this fails: storage file orphaned (acceptable - cleaned during user deletion)
  // ────────────────────────────────────────────────────────────────────────

  const hash = computeFastHash(file);

  const { error: dbError } = await supabase
    .from('image_metadata')
    .insert({
      id: imageId,
      user_id: userId,
      hash,
      storage_url: storageUrl,
      file_type: type,
      mime_type: file.type,
      file_size: file.size,
      uploaded_at: getTimestamp(),
      last_used_at: getTimestamp()
    });

  if (dbError) {
    console.error('Failed to save image metadata:', dbError);
    console.warn(`⚠️  Orphaned storage file at: ${storageUrl} (will be cleaned during user deletion)`);
    throw dbError;
  }

  console.log(`✓ Image uploaded successfully: ${imageId}`);

  return storageUrl;
}

// ══════════════════════════════════════════════════════════════════════════════
// IMAGE RETRIEVAL
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get image metadata by ID
 * @param {string} imageId
 * @returns {Promise<object|null>}
 */
export async function getImageMetadata(imageId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  if (!imageId) return null;

  const userId = await getCurrentUserId();

  const { data, error } = await supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .eq('id', imageId)
    .maybeSingle();

  if (error) throw error;

  return data ? convertImageToCamelCase(data) : null;
}

/**
 * Get image by hash (legacy function - not used for deduplication anymore)
 * Kept for backward compatibility
 * @param {string} hash
 * @returns {Promise<object|null>}
 */
export async function getImageByHash(hash) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  if (!hash) return null;

  const userId = await getCurrentUserId();

  const { data, error } = await supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .eq('hash', hash)
    .eq('archived', false)
    .order('uploaded_at', { ascending: false })
    .maybeSingle();

  if (error) throw error;

  return data ? convertImageToCamelCase(data) : null;
}

/**
 * Get all images
 * @param {object} options - { includeArchived, limit, offset }
 * @returns {Promise<Array>}
 */
export async function getAllImages(options = {}) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  const {
    includeArchived = false,
    limit,
    offset = 0
  } = options;

  const userId = await getCurrentUserId();

  let query = supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .order('uploaded_at', { ascending: false });

  if (!includeArchived) {
    query = query.eq('archived', false);
  }

  if (limit !== undefined) {
    query = query.range(offset, offset + limit - 1);
  }

  const { data, error } = await query;

  if (error) throw error;

  return data ? data.map(convertImageToCamelCase) : [];
}

/**
 * Get images by type
 * @param {string} type - 'logo' | 'signature' | 'watermark'
 * @param {boolean} includeArchived
 * @returns {Promise<Array>}
 */
export async function getImagesByType(type, includeArchived = false) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  const userId = await getCurrentUserId();

  let query = supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .eq('file_type', type)
    .order('uploaded_at', { ascending: false});

  if (!includeArchived) {
    query = query.eq('archived', false);
  }

  const { data, error } = await query;

  if (error) throw error;

  return data ? data.map(convertImageToCamelCase) : [];
}

/**
 * Get image data by ID (for brand/settings hydration)
 * Returns image metadata with storage URL
 * Used by brand.js to hydrate logo/signature/watermark
 * 
 * @param {string|null} imageId - Image ID or null
 * @returns {Promise<object|null>} Image metadata with storageUrl or null
 */
export async function getImageData(imageId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  if (!imageId) return null;

  try {
    const metadata = await getImageMetadata(imageId);
    
    if (!metadata || metadata.archived) {
      return null;
    }

    // Return storage URL and basic metadata
    return {
      id: metadata.id,
      url: metadata.storageUrl,
      storageUrl: metadata.storageUrl,
      fileType: metadata.fileType,
      mimeType: metadata.mimeType,
      fileSize: metadata.fileSize
    };
  } catch (error) {
    console.warn(`Failed to get image data for ID ${imageId}:`, error);
    return null;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// IMAGE DELETION & ARCHIVING (30-DAY RECOVERY)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Archive image (soft delete with 30-day recovery)
 * Note: Storage file is NOT deleted (historical invoice URLs remain valid)
 *
 * @param {string} imageId
 * @returns {Promise<object>} Archived image metadata
 */
export async function archiveImage(imageId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  const userId = await getCurrentUserId();

  const { data: image, error: fetchError } = await supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .eq('id', imageId)
    .single();

  if (fetchError || !image) {
    throw new Error("Image not found");
  }

  if (image.archived) {
    console.warn(`Image ${imageId} is already archived`);
    return convertImageToCamelCase(image);
  }

  const { data, error } = await supabase
    .from('image_metadata')
    .update({
      archived: true,
      archived_at: getTimestamp()
    })
    .eq('user_id', userId)
    .eq('id', imageId)
    .select()
    .single();

  if (error) throw error;

  console.log(`✓ Image archived: ${imageId} (30-day recovery period starts)`);

  return convertImageToCamelCase(data);
}

/**
 * Restore archived image
 * @param {string} imageId
 * @returns {Promise<object>}
 */
export async function restoreImage(imageId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  const userId = await getCurrentUserId();

  const { data: image, error: fetchError } = await supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .eq('id', imageId)
    .single();

  if (fetchError || !image) {
    throw new Error("Image not found");
  }

  if (!image.archived) {
    throw new Error("Image is not archived");
  }

  const { data, error } = await supabase
    .from('image_metadata')
    .update({
      archived: false,
      archived_at: null
    })
    .eq('user_id', userId)
    .eq('id', imageId)
    .select()
    .single();

  if (error) throw error;

  console.log(`✓ Image restored: ${imageId}`);

  return convertImageToCamelCase(data);
}

/**
 * Permanently delete metadata (after 30-day recovery period)
 * Note: Storage file is NOT deleted (historical invoice URLs remain valid)
 *
 * @param {string} imageId
 * @returns {Promise<void>}
 */
export async function permanentlyDeleteMetadata(imageId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  const userId = await getCurrentUserId();

  const { data: image, error: fetchError } = await supabase
    .from('image_metadata')
    .select('*')
    .eq('user_id', userId)
    .eq('id', imageId)
    .single();

  if (fetchError || !image) {
    throw new Error("Image metadata not found");
  }

  const { error } = await supabase
    .from('image_metadata')
    .delete()
    .eq('user_id', userId)
    .eq('id', imageId);

  if (error) throw error;

  console.warn(
    `⚠️  Image metadata deleted: ${imageId} ` +
    `(storage file persists at: ${image.storage_url})`
  );
}

/**
 * Cleanup expired archives (metadata older than 30 days)
 * Run this periodically (e.g. daily cron job)
 *
 * @returns {Promise<number>} Count of metadata rows deleted
 */
export async function cleanupExpiredArchives() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_STORAGE);
  
  const userId = await getCurrentUserId();

  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - RECOVERY_PERIOD_DAYS);

  const { data: expiredImages, error: fetchError } = await supabase
    .from('image_metadata')
    .select('id, storage_url')
    .eq('user_id', userId)
    .eq('archived', true)
    .lt('archived_at', cutoffDate.toISOString());

  if (fetchError) throw fetchError;

  if (!expiredImages || expiredImages.length === 0) {
    console.log('✓ No expired archives to cleanup');
    return 0;
  }

  const idsToDelete = expiredImages.map(img => img.id);

  const { error } = await supabase
    .from('image_metadata')
    .delete()
    .eq('user_id', userId)
    .in('id', idsToDelete);

  if (error) throw error;

  console.log(
    `✓ Cleaned up ${expiredImages.length} expired archive(s) ` +
    `(storage files persist for historical invoices)`
  );

  return expiredImages.length;
}

// ══════════════════════════════════════════════════════════════════════════════
// STATISTICS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get image storage statistics
 * @returns {Promise<object>}
 */
export async function getImageStats() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.IMAGE_ANALYTICS);
  
  const userId = await getCurrentUserId();

  const { data: allImages, error } = await supabase
    .from('image_metadata')
    .select('archived, file_type, file_size')
    .eq('user_id', userId);

  if (error) throw error;

  if (!allImages || allImages.length === 0) {
    return {
      total: 0,
      active: 0,
      archived: 0,
      totalSizeMB: "0.00",
      activeSizeMB: "0.00",
      byType: {
        logo: 0,
        signature: 0,
        watermark: 0
      }
    };
  }

  const active = allImages.filter(img => !img.archived);
  const archived = allImages.filter(img => img.archived);

  const totalBytes = allImages.reduce((sum, img) => sum + (img.file_size || 0), 0);
  const activeBytes = active.reduce((sum, img) => sum + (img.file_size || 0), 0);

  const byType = {
    logo: allImages.filter(img => img.file_type === 'logo').length,
    signature: allImages.filter(img => img.file_type === 'signature').length,
    watermark: allImages.filter(img => img.file_type === 'watermark').length
  };

  return {
    total: allImages.length,
    active: active.length,
    archived: archived.length,
    totalSizeMB: (totalBytes / 1024 / 1024).toFixed(2),
    activeSizeMB: (activeBytes / 1024 / 1024).toFixed(2),
    byType
  };
}

/**
 * Get image validation configuration (for UI)
 * @returns {object}
 */
export function getImageValidationConfig() {
  // ✅ NO PERMISSION CHECK - Public utility function
  return {
    allowedTypes: IMAGE_VALIDATION.ALLOWED_TYPES,
    allowedExtensions: ['jpg', 'jpeg', 'png'],
    maxSizeMB: (IMAGE_VALIDATION.MAX_FILE_SIZE / 1024 / 1024).toFixed(1),
    maxSizeBytes: IMAGE_VALIDATION.MAX_FILE_SIZE
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Validate file before upload
 * @private
 */
function validateFile(file) {
  if (!file || !(file instanceof File)) {
    return { valid: false, error: 'Invalid file object' };
  }

  if (!IMAGE_VALIDATION.ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `File type '${file.type}' not allowed. Allowed: JPG, PNG`
    };
  }

  if (file.size < IMAGE_VALIDATION.MIN_FILE_SIZE) {
    return { valid: false, error: 'File is too small or corrupted' };
  }

  if (file.size > IMAGE_VALIDATION.MAX_FILE_SIZE) {
    const maxMB = (IMAGE_VALIDATION.MAX_FILE_SIZE / 1024 / 1024).toFixed(1);
    const actualMB = (file.size / 1024 / 1024).toFixed(1);
    return {
      valid: false,
      error: `File too large (${actualMB} MB). Maximum: ${maxMB} MB`
    };
  }

  return {
    valid: true,
    sizeMB: (file.size / 1024 / 1024).toFixed(2)
  };
}

/**
 * Compute fast hash (simple fingerprint - for reference only, not used in file path)
 * @private
 */
function computeFastHash(file) {
  // Simple fingerprint: size + sanitized name + timestamp
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9]/g, '');
  const timestamp = file.lastModified || Date.now();
  
  return `${file.size}_${sanitizedName}_${timestamp}`;
}

/**
 * Get file extension
 * @private
 */
function getFileExtension(fileName) {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : 'png';
}

/**
 * Upload file to Supabase Storage
 * @private
 */
async function uploadToStorage(file, fileName) {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, file, {
      contentType: file.type,
      upsert: false, // Never overwrite (each upload has unique filename)
      cacheControl: '31536000' // Cache for 1 year (immutable URLs)
    });

  if (error) {
    console.error('Storage upload failed:', error);
    throw error;
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Convert database row to camelCase
 * @private
 */
function convertImageToCamelCase(image) {
  if (!image) return null;

  return {
    id: image.id,
    userId: image.user_id,
    hash: image.hash,
    storageUrl: image.storage_url,
    fileType: image.file_type,
    mimeType: image.mime_type,
    fileSize: image.file_size,
    uploadedAt: image.uploaded_at,
    lastUsedAt: image.last_used_at,
    archived: image.archived,
    archivedAt: image.archived_at
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED FUNCTIONS (12 total):
 *
 * ✅ Upload:
 * - uploadImage(file, type) - Upload to user folder with unique filename
 *
 * ✅ Read:
 * - getImageMetadata(id) - Get single image metadata
 * - getImageData(id) - Get image data for brand/settings hydration
 * - getImageByHash(hash) - Legacy function (not used for deduplication)
 * - getAllImages(options) - Get all images with pagination
 * - getImagesByType(type, includeArchived) - Filter by type
 *
 * ✅ Delete:
 * - archiveImage(id) - Soft delete (30-day recovery)
 * - restoreImage(id) - Restore archived image
 * - permanentlyDeleteMetadata(id) - Delete metadata after 30 days
 * - cleanupExpiredArchives() - Auto-cleanup job
 *
 * ✅ Stats:
 * - getImageStats() - Storage statistics
 * - getImageValidationConfig() - Validation rules for UI
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * STORAGE STRUCTURE (UPDATED):
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * brand-assets/
 *   a1b2c3d4-uuid/
 *     logo_img_1234_abc.png        ← Used in Invoice #001
 *     logo_img_5678_def.png        ← Used in Invoice #100 (after logo update)
 *     signature_img_9999_xyz.png
 *     watermark_img_7777_pqr.png
 *   e5f6g7h8-uuid/
 *     logo_img_aaaa_bbb.png
 *     signature_img_cccc_ddd.png
 *
 * ADMIN DELETION WORKFLOW:
 * 1. SQL: SELECT permanently_delete_user('a1b2c3d4-uuid');
 * 2. Storage → brand-assets → Delete folder: a1b2c3d4-uuid/
 * 3. Done ✅
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ACID TRANSACTION NOTES:
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Image uploads involve TWO separate systems (Supabase Storage + PostgreSQL):
 *
 * 1. NOT in single ACID transaction (storage and DB are separate services)
 * 2. Fail-fast approach: Upload to storage FIRST, then save metadata
 * 3. If storage fails: Error thrown immediately (no DB changes)
 * 4. If metadata save fails: Orphaned storage file (acceptable trade-off)
 * 5. Orphaned files cleaned up during user deletion (admin deletes entire folder)
 *
 * This two-step process is inherent to storage architecture and is
 * production-standard for file upload systems (AWS S3, Google Cloud Storage, etc.)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */